"use client";

import successAnimation from "@/assets/success-checkmark.json";
import HighlightTitle from "@/components/HighlightTitle";
import { Image } from "@/components/Image";
import Page from "@/components/Page";
import Section from "@/components/ui/Section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { EVENTS, trackEvent } from "@/lib/tracking";
import { tinaField, useTina } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Lottie from "lottie-react";
import { CalendarIcon, CheckIcon, CircleCheck, Clock } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function BookDemoPage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { header, successMessage, benefits } = data.bookDemo;

  return (
    <Page defaultTinaField={data.bookDemo}>
      <Header
        tinaFieldData={data.bookDemo.header}
        successMessage={successMessage}
        successMessageTinaFieldData={data.bookDemo.successMessage}
        {...header}
      />
      <Benefits tinaFieldData={data.bookDemo.benefits} {...benefits} />
    </Page>
  );
}

function Header({ tinaFieldData, title, subtitle, checklist, image, successMessage, successMessageTinaFieldData }) {
  const [isSubmitted, setIsSubmitted] = useState(false);

  return (
    <Section as="header" wrapperClassName="bg-muted !pt-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
        <div className="flex flex-col gap-6 p-6 lg:p-12 relative">
          <div className="absolute w-16 h-10 border-[3px_0_0px_3px] border-primary top-0 left-0" />
          <div className="absolute w-16 h-10 border-[0px_3px_3px_0px] border-primary bottom-0 right-0" />
          <HighlightTitle as="h1" className="h0" content={title} data-tina-field={tinaField(tinaFieldData, "title")} />
          {subtitle && (
            <p data-tina-field={tinaField(tinaFieldData, "subtitle")} className="p-lg">
              {subtitle}
            </p>
          )}
          {checklist?.length > 0 && (
            <ul className="flex flex-col gap-4">
              {checklist.map((item, index) => (
                <li key={item.text?.trim() || `checklist-${index}`} className="flex items-baseline gap-3" data-tina-field={tinaField(checklist[index], "text")}>
                  <div className="bg-primary rounded-full p-1 translate-y-1.5">
                    <CheckIcon className="size-4 shrink-0 text-white" aria-hidden="true" />
                  </div>
                  <span className="">{item.text}</span>
                </li>
              ))}
            </ul>
          )}
          <Image
            src={image}
            alt={title || ""}
            wrapperClassName="max-lg:max-h-[300px] aspect-video mt-4 lg:mt-12"
            className="object-contain rounded-lg"
            sizes="(min-width: 1024px) 50vw, 100vw"
            data-tina-field={tinaField(tinaFieldData, "image")}
            priority
          />
        </div>

        <div className="relative overflow-hidden rounded-lg border border-border bg-white p-6 md:p-8">
          <BookDemoForm onSuccess={() => setIsSubmitted(true)} />
          {isSubmitted && <SuccessMessage tinaFieldData={successMessageTinaFieldData} title={successMessage?.title} text={successMessage?.text} />}
        </div>
      </div>
    </Section>
  );
}

const bookDemoSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  company: z.string().min(1, "Company name is required"),
  date: z.date({ required_error: "Please select a date" }),
  time: z.string().min(1, "Please select a time"),
  details: z.string().optional(),
});

function BookDemoForm({ onSuccess }) {
  const [slots, setSlots] = useState({});
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [userTimezone, setUserTimezone] = useState(null);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [timePopoverOpen, setTimePopoverOpen] = useState(false);

  const form = useForm({
    resolver: zodResolver(bookDemoSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      date: undefined,
      time: "",
      details: "",
    },
  });

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(tz);

    async function fetchSlots() {
      try {
        const { data } = await axios.get(`/api/availability?timezone=${tz}`);
        const parsed = data?.data?.slots || data?.data || null;
        if (parsed && typeof parsed === "object") {
          setSlots(parsed);
        }
      } catch {
        // Leave slots empty so the form stays usable with no times listed
      } finally {
        setSlotsLoading(false);
      }
    }

    fetchSlots();
  }, []);

  const availableDates = Object.keys(slots).map((date) => dayjs(date).toDate());

  const selectedDate = form.watch("date");
  const selectedDateKey = selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : null;
  const availableTimeSlots = selectedDateKey ? slots[selectedDateKey] || [] : [];

  function isDateDisabled(date) {
    if (slotsLoading) return true;
    const dateStr = dayjs(date).format("YYYY-MM-DD");
    return !slots[dateStr] || dayjs(date).isBefore(dayjs(), "day");
  }

  async function onSubmit(values) {
    if (!userTimezone) return;
    try {
      form.clearErrors();
      await axios.post("/api/availability", {
        start: values.time,
        message: values.company ? `Company: ${values.company}\n${values.details || ""}` : values.details || "",
        attendee: {
          name: `${values.firstName} ${values.lastName}`,
          email: values.email,
          timezone: userTimezone,
        },
      });
      trackEvent(EVENTS.BOOK_DEMO_SUBMITTED, {
        form_name: "book_demo",
        page_type: "book_demo",
      });
      form.reset();
      onSuccess?.();
    } catch (error) {
      const errorMessage =
        error.response?.data?.details?.error?.details?.message || error.response?.data?.details?.message || "Something went wrong. Please try again.";
      form.setError("root", { type: "manual", message: errorMessage });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5" aria-label="Book a demo">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  First Name<span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="John" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Last Name<span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Doe" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Work Email<span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@email.com" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="company"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Company<span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter your company name" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Date<span className="text-destructive">*</span>
                </FormLabel>
                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button variant="outline" className="w-full justify-start text-left font-normal h-11">
                        <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        {field.value ? (
                          <span>{dayjs(field.value).format("DD MMM YYYY")}</span>
                        ) : (
                          <span className="text-muted-foreground">{slotsLoading ? "Loading times..." : "Pick a date"}</span>
                        )}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        form.setValue("time", "");
                        setDatePopoverOpen(false);
                      }}
                      disabled={isDateDisabled}
                      defaultMonth={availableDates[0] || new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="time"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Time<span className="text-destructive">*</span>
                </FormLabel>
                <Popover open={timePopoverOpen} onOpenChange={setTimePopoverOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button variant="outline" className="w-full justify-start text-left font-normal h-11" disabled={!selectedDate}>
                        <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                        {field.value ? (
                          <span>{dayjs(field.value).format("HH:mm")}</span>
                        ) : (
                          <span className="text-muted-foreground">{selectedDate ? "Pick a time" : "Select date first"}</span>
                        )}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto max-h-60 overflow-y-auto p-2" align="start">
                    {availableTimeSlots.length > 0 ? (
                      <div className="flex flex-col gap-1" aria-label="Available times">
                        {availableTimeSlots.map((slot) => (
                          <Button
                            key={slot.start}
                            type="button"
                            variant={field.value === slot.start ? "default" : "ghost"}
                            size="sm"
                            className="justify-start"
                            aria-pressed={field.value === slot.start}
                            onClick={() => {
                              field.onChange(slot.start);
                              setTimePopoverOpen(false);
                            }}
                          >
                            {dayjs(slot.start).format("HH:mm")}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <p className="p-2 text-sm text-muted-foreground">{slotsLoading ? "Loading times..." : "No times available"}</p>
                    )}
                  </PopoverContent>
                </Popover>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="details"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Additional Details</FormLabel>
              <FormControl>
                <Textarea
                  rows={3}
                  placeholder="Tell us what's most important to you so we can tailor the demo to your specific needs..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />
        {form.formState.errors.root && (
          <p className="text-[0.8rem] font-medium text-destructive" role="alert">
            {form.formState.errors.root.message}
          </p>
        )}
        <Button type="submit" size="lg" disabled={form.formState.isSubmitting} className="w-full cursor-pointer">
          {form.formState.isSubmitting ? "Booking..." : "Book a Demo"}
        </Button>
        <p className="text-xs leading-relaxed">
          By submitting this form you agree to be contacted in order to explore how SlánDocs can support your operation goals. See our{" "}
          <Link href="/legal/privacy-policy" className="font-medium underline text-foreground">
            Privacy Policy
          </Link>{" "}
          for more information. You may opt out at any time using the &quot;unsubscribe&quot; link in our emails.
        </p>
      </form>
    </Form>
  );
}

function SuccessMessage({ tinaFieldData, title, text }) {
  return (
    <output className="absolute inset-0 flex flex-col items-center justify-center bg-white text-center p-6" aria-live="polite">
      <div className="w-48 h-48">
        <Lottie animationData={successAnimation} loop={false} aria-hidden="true" />
      </div>
      <div className="mt-4 flex flex-col gap-2">
        {title && (
          <h2 className="h2 text-title" data-tina-field={tinaField(tinaFieldData, "title")}>
            {title}
          </h2>
        )}
        {text && (
          <p className="p-lg max-w-[500px] mx-auto" data-tina-field={tinaField(tinaFieldData, "text")}>
            {text}
          </p>
        )}
      </div>
    </output>
  );
}

function Benefits({ tinaFieldData, pretitle, title, subtitle, items }) {
  return (
    <Section>
      <div className="flex flex-col items-start gap-3 mb-10 md:mb-12 max-w-[900px]">
        {pretitle && (
          <Badge variant="outline" data-tina-field={tinaField(tinaFieldData, "pretitle")}>
            {pretitle}
          </Badge>
        )}
        {title && <HighlightTitle as="h2" className="h1 text-balance" content={title} data-tina-field={tinaField(tinaFieldData, "title")} />}
        {subtitle && (
          <p data-tina-field={tinaField(tinaFieldData, "subtitle")} className="p-lg">
            {subtitle}
          </p>
        )}
      </div>
      {items?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {items.map((item, index) => (
            <Card key={item.title?.trim() || `benefit-${index}`} data-tina-field={tinaField(items[index], "title")} className="p-8 flex flex-col gap-4">
              <span className="text-sm font-medium">{String(index + 1).padStart(2, "0")}</span>
              <h3 className="text-title h3">{item.title}</h3>
              <p className="p">{item.text}</p>
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}
